import { isEqual } from 'lodash-es'
import { useEffect, useRef } from 'react'

export interface HotKeyOptions {
  /** Require holding alt key (meta on mac) */
  altKey?: boolean
  /** Require holding control key */
  ctrlKey?: boolean
  /** Require holding shift key */
  shiftKey?: boolean
}

export type HotKeyConfig = {
  key: string
  options?: HotKeyOptions
}

const isMac = navigator.userAgent.indexOf('Mac OS') > -1

/** Listen to key and run callback */
export function useHotKey(config: HotKeyConfig, callback: (e: KeyboardEvent) => void) {
  const { key, options } = config
  const _options = useRef(options)
  if (!isEqual(_options.current, options)) _options.current = options
  const { altKey, ctrlKey, shiftKey } = _options.current ?? {}

  useEffect(() => {
    function listenForHotKeys(e: KeyboardEvent) {
      const alt = isMac ? e.ctrlKey : e.altKey
      const ctrl = isMac ? e.metaKey : e.ctrlKey

      if (e.key?.toLowerCase() !== key.toLowerCase()) return
      if ((altKey && !alt) || (!altKey && alt)) return
      if ((ctrlKey && !ctrl) || (!ctrlKey && ctrl)) return
      if ((shiftKey && !e.shiftKey) || (!shiftKey && e.shiftKey)) return

      e.preventDefault()
      callback(e)
    }

    document.addEventListener('keydown', listenForHotKeys)

    return () => {
      document.removeEventListener('keydown', listenForHotKeys)
    }
  }, [callback, key, _options.current])
}
